import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { BaseAssignee, Filing, FilingReviewStatus } from '../../molecules';
import { AddAssigneeModal } from './AddAssigneeModal';
import { AssigneeInfo } from './AssigneeInfo';

interface ManageAssigneesModalProps {
  isOpen: boolean;
  onClose: () => void;
  filing: Filing;
  availableAssignees: BaseAssignee[];
  onAddAssignees: (assigneeIds: string[]) => Promise<boolean>;
  onRemoveAssignee: (assigneeId: string) => Promise<boolean>;
  onSuccess: () => void;
  allowMultipleAssignees?: boolean;
  loggedInUserEmail: string;
}

export const ManageAssigneesModal = ({
  isOpen,
  onClose,
  filing,
  availableAssignees,
  onAddAssignees,
  onRemoveAssignee,
  onSuccess,
  allowMultipleAssignees = true,
  loggedInUserEmail,
}: ManageAssigneesModalProps) => {
  const addAssigneeModalVisibility = useToggle();

  const isFilingCompletedOrFiled =
    filing.filingStatus === FilingReviewStatus.FILED;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        preventCloseOnOutsideClick
      >
        <DialogHeader className="flex flex-col gap-xs items-start">
          <DialogTitle>Manage Assignees</DialogTitle>
          <DialogDescription>
            View and re-assign team members to{' '}
            <span className="font-bold">{filing.filingName}</span> for{' '}
            {filing.corporation.companyDisplayName}.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4xl p-3xl">
          <AddAssigneeModal
            isOpen={addAssigneeModalVisibility.visible}
            onClose={addAssigneeModalVisibility.toggle}
            filing={filing}
            availableAssignees={availableAssignees}
            onAddAssignees={onAddAssignees}
            onSuccess={onSuccess}
            allowMultipleAssignees={allowMultipleAssignees}
          />
          {filing.agent && (
            <div className="flex flex-col gap-lg">
              <span className="txt-small-bold">Accounting partner</span>
              <div className="border border-border-primary rounded-md py-lg px-xl flex items-center gap-lg">
                <Avatar size="sm" name={filing.agent.name} />
                <span className="txt-small-bold">{filing.agent.name}</span>
              </div>
            </div>
          )}
          <div className="flex flex-col gap-lg">
            <span className="txt-small-bold">Internal assignees</span>
            {!isFilingCompletedOrFiled && (
              <Button
                size="sm"
                variant="link"
                className="text-utility-blue-600 hover:text-utility-blue-700"
                onClick={addAssigneeModalVisibility.toggle}
              >
                <Icons.Plus className="size-4" />
                <span className="txt-small-medium">Add assignees</span>
              </Button>
            )}

            {filing.assignees?.length ? (
              <div className="flex flex-col overflow-clip rounded-md border border-border-primary divide-y divide-border-primary">
                {filing.assignees.map((assignee) => (
                  <AssigneeInfo
                    key={assignee.id}
                    assignee={assignee}
                    filing={filing}
                    onRemoveAssignee={onRemoveAssignee}
                    onSuccess={onSuccess}
                    hideDeleteAssignee={filing.assignees?.length === 1}
                    loggedInUserEmail={loggedInUserEmail}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-lg">
                <span className="txt-small-medium">No assignees</span>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
